import { apiClient } from './client';
import type { Stream, CreateStreamInput, StreamRecordingInfo, StreamScanResult, StreamBulkPatch, StreamPreview, StreamOverride } from '../types';

export async function getStreams(): Promise<Stream[]> {
  const { data } = await apiClient.get<Stream[]>('/api/streams');
  return data;
}

export async function getStream(id: number): Promise<Stream> {
  const { data } = await apiClient.get<Stream>(`/api/streams/${id}`);
  return data;
}

export async function createStream(payload: CreateStreamInput): Promise<Stream> {
  const { data } = await apiClient.post<Stream>('/api/streams', payload);
  return data;
}

export async function updateStream(id: number, payload: Partial<CreateStreamInput>): Promise<Stream> {
  const { data } = await apiClient.put<Stream>(`/api/streams/${id}`, payload);
  return data;
}

export async function deleteStream(id: number): Promise<void> {
  await apiClient.delete(`/api/streams/${id}`);
}

export async function searchStreams(q: string): Promise<Stream[]> {
  const { data } = await apiClient.get<Stream[]>('/api/streams/search', { params: { q } });
  return data;
}

export async function scanStreamURL(url: string, userAgent?: string): Promise<StreamScanResult> {
  const params: Record<string, string> = { url };
  if (userAgent) params.user_agent = userAgent;
  const { data } = await apiClient.get<StreamScanResult>('/api/streams/scan', { params });
  return data;
}

export async function parseStreamsM3U(payload: { m3u?: string; url?: string }): Promise<{ items: StreamPreview[] }> {
  const { data } = await apiClient.post<{ items: StreamPreview[] }>('/api/streams/parse-m3u', payload);
  return data;
}

export async function importStreams(payload: {
  m3u?: string;
  url?: string;
  fallback_bouquet_ids?: number[];
  stream_overrides?: StreamOverride[];
  rename_rule_ids?: number[];
}): Promise<{ imported: number }> {
  const { data } = await apiClient.post<{ imported: number }>('/api/streams/import', payload);
  return data;
}

export async function getStreamBouquets(id: number): Promise<number[]> {
  const { data } = await apiClient.get<number[]>(`/api/streams/${id}/bouquets`);
  return data;
}

export async function setStreamBouquets(id: number, bouquetIds: number[]): Promise<void> {
  await apiClient.put(`/api/streams/${id}/bouquets`, bouquetIds);
}

export async function getStreamPreviewURL(id: number): Promise<{ url: string; source: string }> {
  const { data } = await apiClient.get<{ url: string; source: string }>(`/api/streams/${id}/preview-url`);
  return data;
}

export async function bulkUpdateStreams(payload: { ids: number[]; patch: StreamBulkPatch }): Promise<{ updated: number }> {
  const { data } = await apiClient.patch<{ updated: number }>('/api/streams/bulk', payload);
  return data;
}

export async function bulkDeleteStreams(ids: number[]): Promise<{ deleted: number }> {
  const { data } = await apiClient.delete<{ deleted: number }>('/api/streams/bulk', { data: { ids } });
  return data;
}

export interface BulkResult {
  /** True only when every stream asked for was reached. */
  ok: boolean;
  requested: number;
  succeeded: number;
  /** Ids that don't exist or aren't the caller's. */
  skipped: number;
  failed: { id: number; name: string; error: string }[];
}

export async function bulkStartStreams(ids: number[]): Promise<BulkResult> {
  const { data } = await apiClient.post<BulkResult>('/api/streams/bulk-start', { ids });
  return data;
}

export async function bulkStopStreams(ids: number[]): Promise<BulkResult> {
  const { data } = await apiClient.post<BulkResult>('/api/streams/bulk-stop', { ids });
  return data;
}

export async function bulkRestartStreams(ids: number[]): Promise<BulkResult> {
  const { data } = await apiClient.post<BulkResult>('/api/streams/bulk-restart', { ids });
  return data;
}

export async function startStream(id: number): Promise<void> {
  await apiClient.post(`/api/streams/${id}/start`);
}

export async function stopStream(id: number): Promise<void> {
  await apiClient.post(`/api/streams/${id}/stop`);
}

export async function restartStream(id: number): Promise<void> {
  await apiClient.post(`/api/streams/${id}/restart`);
}

export async function killStreamConnections(id: number): Promise<void> {
  await apiClient.delete(`/api/streams/${id}/connections`);
}

export async function getStreamSys(id: number): Promise<import('../types').StreamSys> {
  const { data } = await apiClient.get<import('../types').StreamSys>(`/api/streams/${id}/sys`);
  return data;
}

export interface StreamStats {
  stream_id: number;
  active_now: number;
  views_24h: number;
  unique_viewers_24h: number;
  watch_secs_24h: number;
  views_7d: number;
  unique_viewers_7d: number;
  watch_secs_7d: number;
}

export async function getStreamStats(id: number): Promise<StreamStats> {
  const { data } = await apiClient.get<StreamStats>(`/api/streams/${id}/stats`);
  return data;
}

export async function getStreamActivity(id: number): Promise<import('../types').UserActivity[]> {
  const { data } = await apiClient.get<import('../types').UserActivity[]>(`/api/streams/${id}/activity`);
  return data;
}

export async function probeStream(id: number): Promise<void> {
  await apiClient.post(`/api/streams/${id}/probe`);
}

export async function getStreamRecording(id: number): Promise<StreamRecordingInfo> {
  const { data } = await apiClient.get<StreamRecordingInfo>(`/api/streams/${id}/record`);
  return data;
}

export async function startStreamRecording(id: number, minutes: number): Promise<void> {
  await apiClient.post(`/api/streams/${id}/record`, { minutes });
}

export async function stopStreamRecording(id: number): Promise<void> {
  await apiClient.delete(`/api/streams/${id}/record`);
}

export function exportStreamsM3UUrl(params: { categoryId?: string; bouquetId?: string; enabledOnly?: boolean } = {}): string {
  const p = new URLSearchParams();
  if (params.categoryId) p.set('category_id', params.categoryId);
  if (params.bouquetId) p.set('bouquet_id', params.bouquetId);
  if (params.enabledOnly) p.set('enabled_only', 'true');
  const qs = p.toString();
  return `/api/streams/export.m3u${qs ? '?' + qs : ''}`;
}

export async function downloadStreamsM3U(params: { categoryId?: string; bouquetId?: string; enabledOnly?: boolean } = {}): Promise<void> {
  const url = exportStreamsM3UUrl(params);
  const { data } = await apiClient.get<Blob>(url, { responseType: 'blob' });
  const href = URL.createObjectURL(data);
  const a = document.createElement('a');
  a.href = href;
  a.download = 'streams_export.m3u';
  a.click();
  URL.revokeObjectURL(href);
}

/** Latest snapshot from the stream's thumbnail server, as an object URL (the endpoint needs the auth header). */
export async function getStreamThumbnail(id: number): Promise<string> {
  const { data } = await apiClient.get<Blob>(`/api/streams/${id}/thumbnail`, { responseType: 'blob' });
  return URL.createObjectURL(data);
}
